import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { OfficerDto, Paginated } from '@pams/shared';
import { labelEntriesOf, useLabelCache } from '@/hooks/useLabelCache';
import { useDebounced } from '@/hooks/useDebounced';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { withChosenOption } from '@/lib/selectOptions';

export type OfficerSearchSelectProps = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  /**
   * What to call the chosen officer when neither the search result nor this session knows them —
   * a value that arrived from the URL. The caller usually has the name already (the Audit log's
   * rows carry `actorName`), so the page never has to ask the server twice.
   */
  fallbackLabel?: string | null;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
};

/** The one search the picker issues — bounded, and the same page size the command picker uses. */
const SEARCH_LIMIT = 20;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [ANALYTICS §8] ONE OFFICER, CHOSEN BY SEARCH — THE AUDIT LOG'S "WHO" FILTER.
 *
 *  A single-value sibling of the command-role `OfficerPicker` in `intake/Sections.tsx`, and it
 *  obeys that picker's three rules rather than growing a fourth:
 *
 *  ⚠️ NEVER A PHONE. The option row names the officer and says `rank · unit` beneath; `/officers`
 *  returns `phone` on every row and this component reads nothing of it. A phone typed into the box
 *  still MATCHES — [C4a §2b]'s "matching on a number is not disclosing one" — and a test holds that
 *  no digits of it reach the rendered options.
 *
 *  ⚠️ THE LABEL IS REMEMBERED WHEN THE OFFICER IS PICKED (`useLabelCache`). The result list rotates
 *  with the next keystroke, and rc-select ends `label ?? item.value` — so without the cache the
 *  chosen officer repaints as an ObjectId (divergence #84). A value controlled from the URL with no
 *  session pick behind it takes `fallbackLabel`, and failing that `No longer available`.
 *
 *  ⚠️ THE DEBOUNCE IS ON THE VALUE, `useDebounced`'s rule, so TanStack's cache and cancellation keep
 *  working. And `filterOption={false}`: the server decides what matches, and a client filter over a
 *  search result filters twice.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OfficerSearchSelect = ({
  value,
  onChange,
  fallbackLabel,
  placeholder = 'Search by name or phone',
  'aria-label': ariaLabel,
  className,
}: OfficerSearchSelectProps) => {
  const [search, setSearch] = useState('');
  const term = useDebounced(search.trim());
  const params = { page: 1, limit: SEARCH_LIMIT, ...(term ? { search: term } : {}) };

  const officers = useQuery({
    queryKey: queryKeys.officers.list(params),
    queryFn: ({ signal }) => api.get<Paginated<OfficerDto>>('/officers', { query: params, signal }),
    staleTime: 30_000,
  });

  const { labels, remember } = useLabelCache();
  const byId = new Map((officers.data?.items ?? []).map((officer) => [officer.id, officer]));

  const options = withChosenOption(
    (officers.data?.items ?? []).map((officer) => ({ value: officer.id, label: officer.name })),
    value,
    (id) => {
      const label = labels.get(id) ?? fallbackLabel ?? undefined;
      return label ? { name: label } : undefined;
    },
  );

  return (
    <Select<string>
      showSearch
      allowClear
      className={className ?? 'w-full'}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      filterOption={false}
      onSearch={setSearch}
      loading={officers.isFetching}
      onChange={(next: string | undefined, option: unknown) => {
        remember(labelEntriesOf(option));
        /* The search that found them is spent; the next one starts from an empty box. */
        setSearch('');
        onChange?.(next);
      }}
      notFoundContent={officers.isFetching ? 'Searching…' : 'No officers match'}
      options={options}
      optionRender={(option) => {
        const officer = byId.get(String(option.value));
        const detail = [officer?.rank?.shortName, officer?.orgUnit?.name].filter(Boolean).join(' · ');
        return (
          <div className="flex min-w-0 flex-col">
            <span className="truncate">{officer?.name ?? option.label}</span>
            {detail && <span className="truncate text-xs text-muted-foreground">{detail}</span>}
          </div>
        );
      }}
      data-testid="officer-search-select"
    />
  );
};
